import { Link } from '@tanstack/react-router';
import { CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { CONTENT_STATUSES } from '@marketingbee/contracts';
import { Alert, Button, Card, CardContent, EmptyState, PageHeader, cn, useToast } from '@marketingbee/ui';
import { QueryView } from '../components/common';
import { addDays, BrandPicker, ChannelBadge, localDate, pillarName, STATUS_CLASSES, STATUS_LABELS, timeInZone, useActiveBrand, weekStartOf, zonedToUtc, type BrandRow } from '../components/marketing';
import { errorMessage, trpc, type RouterOutputs } from '../lib/trpc';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function CalendarPage() {
  const { list, brands, brand, setBrandId } = useActiveBrand();
  return (
    <div className="space-y-6">
      <QueryView query={list} label="Loading brands">
        {() =>
          brand ? (
            <WeekView key={brand.id} brand={brand} picker={<BrandPicker brands={brands} value={brand.id} onChange={setBrandId} />} />
          ) : (
            <>
              <PageHeader title="Calendar" />
              <Card>
                <EmptyState title="Set up your brand first" description="The weekly plan comes from your Brand Brain: who you talk to, what about and how often." action={<Button asChild><Link to="/brand">Set up the brand</Link></Button>} />
              </Card>
            </>
          )
        }
      </QueryView>
    </div>
  );
}

function WeekView({ brand, picker }: { brand: BrandRow; picker: React.ReactNode }) {
  const tz = brand.profile.timezone;
  const thisWeek = weekStartOf(new Date(), tz);
  const [week, setWeek] = useState(thisWeek);
  const [result, setResult] = useState<RouterOutputs['content']['plan'] | null>(null);
  const from = zonedToUtc(week, '00:00', tz);
  const to = zonedToUtc(addDays(week, 7), '00:00', tz);
  const items = trpc.content.list.useQuery({ brandId: brand.id, from, to, limit: 200 });
  const plan = trpc.content.plan.useMutation();
  const utils = trpc.useUtils();
  const { error } = useToast();
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const today = localDate(new Date(), tz);
  const isPast = addDays(week, 7) <= today;
  const planLabel = week === thisWeek ? 'Plan this week' : week === addDays(thisWeek, 7) ? 'Plan next week' : 'Plan this week';
  const rangeLabel = `${fmtDay(days[0]!)} – ${fmtDay(days[6]!)}`;

  return (
    <>
      <PageHeader
        title="Calendar"
        description={`Week of ${rangeLabel} · times in ${tz.replace(/_/g, ' ')}`}
        actions={
          <>
            {picker}
            <Button
              disabled={isPast}
              loading={plan.isPending}
              onClick={() =>
                plan.mutate(
                  { brandId: brand.id, weekStart: week },
                  {
                    onSuccess: async (r) => {
                      setResult(r);
                      await Promise.all([utils.content.list.invalidate(), utils.dashboard.summary.invalidate()]);
                    },
                    onError: (e) => error('Could not plan the week', errorMessage(e)),
                  },
                )
              }
            >
              <CalendarPlus /> {planLabel}
            </Button>
          </>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" aria-label="Previous week" onClick={() => { setWeek(addDays(week, -7)); setResult(null); }}>
          <ChevronLeft /> Previous
        </Button>
        <Button variant="outline" size="sm" disabled={week === thisWeek} onClick={() => { setWeek(thisWeek); setResult(null); }}>
          This week
        </Button>
        <Button variant="outline" size="sm" aria-label="Next week" onClick={() => { setWeek(addDays(week, 7)); setResult(null); }}>
          Next <ChevronRight />
        </Button>
        <Legend />
      </div>
      {result ? (
        <Alert variant="success" title={result.planned ? `Planned ${result.planned} post${result.planned === 1 ? '' : 's'} for the week of ${fmtDay(result.weekStart)}` : 'This week is already planned'}>
          {result.drafted} drafted and sent for review, {result.queued} waiting for the writer. Human-only pillars are left for you to write. Nothing goes out until it is approved.
        </Alert>
      ) : null}
      <QueryView query={items} label="Loading the week">
        {(rows) => (
          <div className="grid gap-3 md:grid-cols-7">
            {days.map((d, i) => {
              const dayRows = rows.filter((r) => r.scheduledAt && localDate(new Date(r.scheduledAt), tz) === d);
              return (
                <Card key={d} className={cn('min-h-32', d === today && 'ring-2 ring-primary/60')}>
                  <CardContent className="space-y-2 p-3">
                    <p className="text-[13px] font-semibold">
                      {DAY_NAMES[i]} <span className="font-normal text-muted-foreground">{fmtDay(d)}</span>
                    </p>
                    {dayRows.length === 0 ? <p className="text-[12px] text-muted-foreground">Nothing planned</p> : null}
                    {dayRows.map((r) => (
                      <Link
                        key={r.id}
                        to="/content/$id"
                        params={{ id: r.id }}
                        className={cn('block rounded-lg px-2 py-1.5 text-[12px] leading-snug transition-[filter] hover:brightness-95', STATUS_CLASSES[r.status])}
                        title={`${STATUS_LABELS[r.status]}: ${r.title ?? r.body.slice(0, 80)}`}
                      >
                        <span className="flex items-center justify-between gap-1">
                          <span className="font-mono">{timeInZone(new Date(r.scheduledAt!), tz)}</span>
                          <ChannelBadge channel={r.channel} className="bg-card/70 px-1.5 py-0 text-[11px]" />
                        </span>
                        <span className="mt-0.5 block truncate">{r.humanOnly && !r.body ? 'Write this one yourself' : (r.title ?? (r.body.slice(0, 60) || pillarName(brand.profile, r.pillarId) || 'Planned'))}</span>
                        <span className="block text-[11px] opacity-80">{STATUS_LABELS[r.status]}</span>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </QueryView>
    </>
  );
}

function Legend() {
  const shown = CONTENT_STATUSES.filter((s) => s !== 'archived' && s !== 'approved');
  return (
    <div className="ml-auto hidden flex-wrap gap-1.5 lg:flex" aria-label="Status colours">
      {shown.map((s) => (
        <span key={s} className={cn('rounded-md px-1.5 py-0.5 text-[11px]', STATUS_CLASSES[s])}>
          {s === 'scheduled' ? 'Approved / scheduled' : STATUS_LABELS[s]}
        </span>
      ))}
    </div>
  );
}

function fmtDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('en-CA', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}
